import React, { useEffect, useRef, useState } from "react";

/**
 * Single-line partner field: click to edit, Enter/blur to save, Esc to cancel.
 * No success toast — errors surface via onSave rejection (parent shows swal).
 */
const PartnerDetailInlineValue = ({
  id,
  value,
  onSave,
  busy = false,
  disabled = false,
  display = "text",
  emptyLabel = "Not set",
  inputType,
  inputMode,
  autoComplete = "off",
  className = "",
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const resolvedInputType =
    inputType || (display === "email" ? "email" : display === "url" ? "url" : "text");

  useEffect(() => {
    if (!editing) {
      setDraft(value != null ? String(value) : "");
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try {
        inputRef.current.select();
      } catch {
        /* ignore */
      }
    }
  }, [editing]);

  const viewText = value != null && String(value).trim() !== "" ? String(value).trim() : "";

  const cancel = () => {
    setDraft(value != null ? String(value) : "");
    setEditing(false);
  };

  const commit = async () => {
    if (busy || disabled) return;
    const next = String(draft).trim();
    const prev = viewText;
    if (next === prev) {
      setEditing(false);
      return;
    }
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      /* parent already surfaced error */
    }
  };

  if (disabled) {
    if (!viewText) {
      return <span className={`text-soft ${className}`.trim()}>{emptyLabel}</span>;
    }
    if (display === "url") {
      const href = /^https?:\/\//i.test(viewText) ? viewText : `https://${viewText}`;
      return (
        <a className={className} href={href} target="_blank" rel="noopener noreferrer">
          {viewText}
        </a>
      );
    }
    if (display === "email") {
      return (
        <a className={className} href={`mailto:${encodeURIComponent(viewText)}`}>
          {viewText}
        </a>
      );
    }
    return <span className={className}>{viewText}</span>;
  }

  if (editing) {
    return (
      <span className={`partner-detail-inline partner-detail-inline--editing ${className}`.trim()}>
        <input
          ref={inputRef}
          id={id}
          type={resolvedInputType}
          inputMode={inputMode}
          autoComplete={autoComplete}
          className="form-control form-control-sm partner-detail-inline__input"
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            void commit();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void commit();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
        />
        {busy ? <span className="spinner-border spinner-border-sm text-primary ml-2 align-middle" role="status" aria-label="Saving" /> : null}
      </span>
    );
  }

  return (
    <span className={`partner-detail-inline ${className}`.trim()}>
      <button
        type="button"
        className="btn btn-link btn-sm text-left p-0 align-baseline partner-detail-inline__toggle"
        disabled={busy}
        aria-label={viewText ? "Edit value" : `Set value (${emptyLabel})`}
        onClick={() => {
          if (busy) return;
          setEditing(true);
        }}
      >
        {viewText ? <span className="text-break">{viewText}</span> : <span className="text-soft">{emptyLabel}</span>}
      </button>
      {viewText && display === "url" ? (
        <a
          className="btn btn-sm btn-light ml-2 py-0 px-1 align-baseline"
          href={/^https?:\/\//i.test(viewText) ? viewText : `https://${viewText}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          Open
        </a>
      ) : null}
      {viewText && display === "email" ? (
        <a
          className="btn btn-sm btn-light ml-2 py-0 px-1 align-baseline"
          href={`mailto:${encodeURIComponent(viewText)}`}
          onClick={(e) => e.stopPropagation()}
        >
          Mail
        </a>
      ) : null}
    </span>
  );
};

export default PartnerDetailInlineValue;
